import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { CardSkeleton, ListSkeleton } from "./ui/skeleton";
import { useDebounce } from "../hooks/useDebounce";
import { useMobile } from "../hooks/useMobile";

export default function Pipeline({ api }) {
  // Mobile detection
  const { isMobile, isTablet } = useMobile();

  // Basic state
  const [draggedLead, setDraggedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState("all");
  const [funnelsWithLeads, setFunnelsWithLeads] = useState(new Set());
  const [funnelsWithPipelines, setFunnelsWithPipelines] = useState(new Set());
  const [selectedPipeline, setSelectedPipeline] = useState(null);

  // Stage automation (auto-advance leads that are stuck in a stage)
  const [automationDialogStage, setAutomationDialogStage] = useState(null);
  const [automationForm, setAutomationForm] = useState({ enabled: false, days: 3 });
  const [savingAutomation, setSavingAutomation] = useState(false);

  // Modal states
  const [showLeadDetailsModal, setShowLeadDetailsModal] = useState(false);
  const [showAddInteractionModal, setShowAddInteractionModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadActivities, setLeadActivities] = useState([]);

  // Interaction form state
  const [newInteraction, setNewInteraction] = useState({
    type: "call",
    description: "",
    scheduled_date: "",
    scheduled_time: "",
  });

  // Search debouncing - 2 seconds to avoid too many API calls while typing
  const debouncedSearchTerm = useDebounce(searchTerm, 2200);

  // Leads state (no pagination for Kanban)
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all leads without pagination
  const fetchAllLeads = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use the stage ID directly as status value — stage IDs are kept in sync
      // with the actual lead.status values stored in the database.
      const statusFilterValue = stageFilter !== "all" ? stageFilter : undefined;

      const queryParams = new URLSearchParams({
        limit: 1000,
        ...(selectedFunnel &&
          selectedFunnel !== "all" && { funnel_id: selectedFunnel }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(statusFilterValue && { status: statusFilterValue }),
        ...(dateFilter && dateFilter !== "all" && { date_filter: dateFilter }),
      });

      const response = await api.get(`/leads?${queryParams}`);

      let leadsData = [];
      if (response.data && response.data.items) {
        leadsData = response.data.items;
      } else if (Array.isArray(response.data)) {
        leadsData = response.data;
      }

      setLeads(leadsData);

      const funnelIdsWithLeads = new Set(
        leadsData.map((l) => l.funnel_id).filter(Boolean),
      );
      setFunnelsWithLeads(funnelIdsWithLeads);
    } catch (err) {
      console.error("[Pipeline] Error fetching leads:", err);
      setError(err.message || "Failed to fetch leads");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [api, selectedFunnel, debouncedSearchTerm, stageFilter, dateFilter]);

  // Refresh function
  const refresh = useCallback(() => {
    fetchAllLeads();
  }, [fetchAllLeads]);

  // Get stages - from pipeline if specific funnel selected, or default for "all"
  const getStages = () => {
    // If a specific funnel with pipeline is selected, use pipeline stages
    if (
      selectedPipeline?.stages &&
      selectedFunnel &&
      selectedFunnel !== "all"
    ) {
      return selectedPipeline.stages.map((stage, index) => {
        const stageColors = [
          "from-sky-300 to-sky-400",
          "from-sky-400 to-cyan-500",
          "from-cyan-500 to-teal-500",
          "from-yellow-400 to-amber-500",
          "from-orange-400 to-orange-600",
          "from-red-400 to-red-500",
          "from-red-600 to-red-800",
        ];
        return {
          id: stage.name.toLowerCase().replace(/\s+/g, "_"),
          name: stage.name,
          label: stage.name,
          color: stageColors[index] || "from-slate-500 to-gray-600",
          position: stage.position,
          rawId: stage.id,
          autoAdvanceEnabled: !!stage.auto_advance_enabled,
          autoAdvanceDays: stage.auto_advance_days || null,
          isLastStage: index === selectedPipeline.stages.length - 1,
        };
      });
    }

    // Default stages for "all" view.
    // IDs MUST match the lead.status values stored in the database so that
    // getLeadsByStage() can match leads to columns without a separate mapping.
    return [
      {
        id: "new",
        name: "Novo",
        label: "Novo",
        color: "from-sky-300 to-sky-400",
        position: 0,
      },
      {
        id: "contacted",
        name: "Contatado",
        label: "Contatado",
        color: "from-sky-400 to-cyan-500",
        position: 1,
      },
      {
        id: "qualified",
        name: "Qualificado",
        label: "Qualificado",
        color: "from-cyan-500 to-teal-500",
        position: 2,
      },
      {
        id: "proposal",
        name: "Proposta",
        label: "Proposta",
        color: "from-yellow-400 to-amber-500",
        position: 3,
      },
      {
        id: "negotiation",
        name: "Negociação",
        label: "Negociação",
        color: "from-orange-400 to-orange-600",
        position: 4,
      },
      {
        id: "won",
        name: "Ganho",
        label: "Ganho",
        color: "from-red-400 to-red-500",
        position: 5,
      },
      {
        id: "lost",
        name: "Perdido",
        label: "Perdido",
        color: "from-red-600 to-red-800",
        position: 6,
      },
    ];
  };

  const stages = getStages();

  // Open the "auto-advance" config dialog for a stage
  const openAutomationDialog = (stage) => {
    setAutomationDialogStage(stage);
    setAutomationForm({
      enabled: stage.autoAdvanceEnabled,
      days: stage.autoAdvanceDays || 3,
    });
  };

  // Persist the auto-advance config for the stage being edited
  const saveAutomationConfig = async () => {
    if (!automationDialogStage || !selectedPipeline) return;

    if (automationForm.enabled && (!automationForm.days || Number(automationForm.days) < 1)) {
      toast.error("Informe uma quantidade de dias válida (mínimo 1)");
      return;
    }

    setSavingAutomation(true);
    try {
      const response = await api.put(
        `/pipelines/${selectedPipeline.id}/stages/${automationDialogStage.rawId}/automation`,
        {
          enabled: automationForm.enabled,
          days: automationForm.enabled ? Number(automationForm.days) : null,
        },
      );
      setSelectedPipeline(response.data);
      toast.success(
        automationForm.enabled
          ? `Automação ativada: leads em "${automationDialogStage.label}" avançam sozinhos após ${automationForm.days} dia(s) parados.`
          : "Automação desativada.",
      );
      setAutomationDialogStage(null);
    } catch (error) {
      console.error("Error saving stage automation:", error);
      toast.error(
        error.response?.data?.detail || "Erro ao salvar automação da etapa",
      );
    } finally {
      setSavingAutomation(false);
    }
  };

  // Fetch leads on mount and when filters or funnel change
  useEffect(() => {
    fetchAllLeads();
  }, [debouncedSearchTerm, stageFilter, dateFilter, selectedFunnel]);

  const getLeadsByStage = (stageId) => {
    if (!leads || leads.length === 0) {
      return [];
    }

    const normalize = (str) =>
      (str || "").toLowerCase().trim().replace(/\s+/g, "_");

    // If pipeline exists (specific funnel selected), match lead status with pipeline stage
    if (
      selectedPipeline &&
      selectedPipeline.stages &&
      selectedFunnel &&
      selectedFunnel !== "all"
    ) {
      const targetStage = selectedPipeline.stages.find(
        (s) => normalize(s.name) === normalize(stageId) || s.id === stageId,
      );

      if (!targetStage) return [];

      const stageNorm = normalize(targetStage.name);

      // Filter leads - only exact match (normalized) to avoid duplicates
      const result = leads.filter((lead) => {
        if (!lead.funnel_id || lead.funnel_id !== selectedFunnel) return false;
        if (!lead.status) return false;

        const leadStatusNorm = normalize(lead.status);

        // Only exact match after normalization - each lead belongs to ONE stage only
        return leadStatusNorm === stageNorm;
      });

      return result;
    }

    // "All" or no pipeline selected - match by normalized status with all leads
    return leads.filter(
      (lead) => lead.status && normalize(lead.status) === normalize(stageId),
    );
  };

  // Load funnels
  const fetchFunnels = useCallback(async () => {
    try {
      const response = await api.get("/funnels/list");
      setFunnels(response.data);
    } catch (error) {
      console.error("Error fetching funnels:", error);
      toast.error("Erro ao carregar funis");
    }
  }, [api]);

  // Load pipeline by funnel - create if not found
  const fetchPipelineByFunnel = useCallback(
    async (funnelId) => {
      if (!funnelId) {
        setSelectedPipeline(null);
        return;
      }

      try {
        const response = await api.get(`/pipelines/by-funnel/${funnelId}`);
        setSelectedPipeline(response.data);
        setFunnelsWithPipelines((prev) => new Set([...prev, funnelId]));
      } catch (error) {
        // Pipeline not found - try to create it
        if (error.response && error.response.status === 404) {
          console.log(
            "[Pipeline] Pipeline not found, creating from funnel stages...",
          );

          // Find the funnel to get its stages
          const funnel = funnels.find((f) => f.id === funnelId);
          if (funnel && funnel.stages && funnel.stages.length > 0) {
            try {
              // Create pipeline with stages from funnel
              const pipelineData = {
                name: `Pipeline - ${funnel.name}`,
                description: `Pipeline automático criado para o funil: ${funnel.name}`,
                stages: funnel.stages.map((stage, index) => ({
                  name: stage.name || stage,
                  probability: Math.round(
                    ((index + 1) / funnel.stages.length) * 100,
                  ),
                  position: index,
                })),
                funnel_id: funnelId,
                is_default: false,
              };

              console.log(
                "[Pipeline] Creating pipeline with data:",
                pipelineData,
              );
              const createResponse = await api.post("/pipelines", pipelineData);
              setSelectedPipeline(createResponse.data);
              setFunnelsWithPipelines((prev) => new Set([...prev, funnelId]));
              toast.success("Pipeline criado automaticamente!");
            } catch (createError) {
              console.error("[Pipeline] Error creating pipeline:", createError);
              toast.error(`Erro ao criar pipeline: ${createError.message}`);
              setSelectedPipeline(null);
            }
          } else {
            console.error("[Pipeline] Funnel not found or has no stages");
            toast.error("Funil não encontrado ou sem estágios");
            setSelectedPipeline(null);
          }
        } else {
          console.error("[Pipeline] Error fetching pipeline:", error);
          toast.error("Erro ao carregar pipeline");
          setSelectedPipeline(null);
        }
      }
    },
    [api, funnels],
  );

  // Handle funnel selection
  const handleFunnelChange = (funnelId) => {
    setSelectedFunnel(funnelId);
    if (funnelId === "all" || !funnelId) {
      setSelectedPipeline(null);
    } else {
      fetchPipelineByFunnel(funnelId);
    }
    // Fetch leads immediately when funnel changes
    fetchAllLeads();
  };

  // Load funnels on component mount
  useEffect(() => {
    fetchFunnels();
  }, [fetchFunnels]);

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  // Moves a lead to another stage — used both by drag-and-drop (desktop) and
  // the stage picker (mobile/touch, where native HTML5 drag doesn't work).
  const moveLeadToStage = async (lead, targetStage) => {
    if (!lead) return;

    const targetStageObj = stages.find((s) => s.id === targetStage);
    const newStatus = targetStageObj?.name || targetStage;

    if (lead.status === newStatus) return;

    try {
      await api.put(`/leads/${lead.id}`, {
        status: newStatus,
      });

      // Update local state immediately for instant feedback
      setLeads((prevLeads) =>
        prevLeads.map((l) =>
          l.id === lead.id ? { ...l, status: newStatus } : l,
        ),
      );

      toast.success(`Lead movido para ${targetStageObj?.label || targetStage}`);
    } catch (error) {
      toast.error("Erro ao mover lead");
      console.error(error);
      // Revert on error
      refresh();
    }
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();

    if (!draggedLead) {
      setDraggedLead(null);
      return;
    }

    await moveLeadToStage(draggedLead, targetStage);
    setDraggedLead(null);
  };

  const getStageStats = (stageId) => {
    const stageLeads = getLeadsByStage(stageId);
    return {
      count: stageLeads.length,
      percentage:
        leads.length > 0
          ? Math.round((stageLeads.length / leads.length) * 100)
          : 0,
    };
  };

  // View lead details
  const handleViewLeadDetails = async (lead) => {
    try {
      setSelectedLead(lead);

      // Fetch lead activities
      const response = await api.get(`/leads/${lead.id}/activities`);
      setLeadActivities(response.data || []);

      setShowLeadDetailsModal(true);
    } catch (error) {
      console.error("Error fetching lead details:", error);
      toast.error("Erro ao carregar detalhes do lead");
    }
  };

  // Add interaction to lead
  const handleAddInteraction = (lead) => {
    setSelectedLead(lead);
    setNewInteraction({
      type: "call",
      description: "",
      scheduled_date: "",
      scheduled_time: "",
    });
    setShowAddInteractionModal(true);
  };

  // Save new interaction
  const handleSaveInteraction = async () => {
    if (!newInteraction.description.trim()) {
      toast.error("Descrição da interação é obrigatória");
      return;
    }

    try {
      // Combine date and time if provided
      let scheduledDateTime = null;
      if (newInteraction.scheduled_date && newInteraction.scheduled_time) {
        scheduledDateTime = `${newInteraction.scheduled_date}T${newInteraction.scheduled_time}:00`;
      } else if (newInteraction.scheduled_date) {
        scheduledDateTime = `${newInteraction.scheduled_date}T09:00:00`;
      }

      const interactionData = {
        lead_id: selectedLead.id,
        type: newInteraction.type,
        description: newInteraction.description,
        ...(scheduledDateTime && { scheduled_date: scheduledDateTime }),
      };

      await api.post("/activities", interactionData);
      toast.success("Interação adicionada com sucesso!");

      setShowAddInteractionModal(false);
      setNewInteraction({
        type: "call",
        description: "",
        scheduled_date: "",
        scheduled_time: "",
      });

      // Refresh if details modal is open
      if (showLeadDetailsModal) {
        handleViewLeadDetails(selectedLead);
      }
    } catch (error) {
      console.error("Error saving interaction:", error);
      toast.error("Erro ao salvar interação");
    }
  };

  // Loading state with skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-slate-200 rounded w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-slate-200 rounded w-48 animate-pulse"></div>
          </div>
          <div className="h-10 bg-slate-200 rounded w-24 animate-pulse"></div>
        </div>

        {/* Pipeline skeleton - responsive grid */}
        <div
          className={`grid gap-4 ${
            isMobile
              ? "grid-cols-1"
              : isTablet
                ? "grid-cols-2 md:grid-cols-3"
                : "grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7"
          }`}
        >
          {stages.map((stage, i) => (
            <CardSkeleton key={i} showHeader={true} lines={3} />
          ))}
        </div>
      </div>
    );
  }

  // Show all funnels in dropdown (no filter)
  const validFunnels = funnels;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pipeline-page">
      {/* Header */}
      <div
        className={`flex items-center justify-between ${isMobile ? "flex-col space-y-4" : "flex-row"}`}
      >
        <div className={isMobile ? "text-center" : ""}>
          <h1
            className={`font-bold text-slate-900 ${isMobile ? "text-2xl" : "text-3xl"}`}
          >
            Pipeline de Vendas
          </h1>
          <p className="text-slate-600 mt-1">
            Visualize e gerencie seus leads por estágio
          </p>
        </div>
        <Button
          onClick={refresh}
          disabled={loading}
          variant="outline"
          className="bg-white/60 backdrop-blur-sm border-white/30 hover:bg-white/80"
          data-testid="refresh-pipeline-button"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Atualizar
        </Button>
      </div>

      {/* Funnel Selection */}
      <Card className="glass border-white/20">
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Label
                htmlFor="funnel-select"
                className="text-sm font-medium text-slate-700 mb-2 block"
              >
                Selecionar Funil
              </Label>
              <Select value={selectedFunnel} onValueChange={handleFunnelChange}>
                <SelectTrigger className="bg-white/60 border-white/30">
                  <SelectValue placeholder="Escolha um funil para visualizar seu pipeline" />
                </SelectTrigger>
                <SelectContent>
                  {validFunnels.length === 0 && funnels.length > 0 ? (
                    <div className="p-2 text-sm text-slate-500 text-center">
                      Nenhum funil com leads encontrados
                    </div>
                  ) : (
                    <>
                      <SelectItem value="all">
                        Todos os leads (pipeline padrão)
                      </SelectItem>
                      {validFunnels.map((funnel) => (
                        <SelectItem key={funnel.id} value={funnel.id}>
                          {funnel.name} ({funnel.type})
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedPipeline && (
              <div className="text-right">
                <p className="text-sm font-medium text-slate-700">
                  Pipeline Ativo:
                </p>
                <p className="text-lg font-bold text-slate-900">
                  {selectedPipeline.name}
                </p>
                <p className="text-xs text-slate-500">
                  {selectedPipeline.stages?.length || 0} estágios
                </p>
              </div>
            )}
          </div>

          {selectedFunnel && selectedFunnel !== "all" && !selectedPipeline && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                ⚠️ Pipeline não encontrado para este funil. Verifique se foi
                criado automaticamente.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="glass border-white/20">
        <CardContent className="p-4">
          <div
            className={`grid gap-4 ${isMobile ? "grid-cols-1" : "grid-cols-2 lg:grid-cols-4"}`}
          >
            <div>
              <Label
                htmlFor="search"
                className="text-sm font-medium text-slate-700 mb-2 block"
              >
                Buscar Leads
              </Label>
              <Input
                id="search"
                type="text"
                placeholder="Nome, email ou empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white/60 border-white/30"
              />
            </div>

            <div>
              <Label
                htmlFor="stage-filter"
                className="text-sm font-medium text-slate-700 mb-2 block"
              >
                Filtrar por Estágio
              </Label>
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="bg-white/60 border-white/30">
                  <SelectValue placeholder="Todos os estágios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estágios</SelectItem>
                  {stages.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label
                htmlFor="date-filter"
                className="text-sm font-medium text-slate-700 mb-2 block"
              >
                Filtrar por Data
              </Label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="bg-white/60 border-white/30">
                  <SelectValue placeholder="Todas as datas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as datas</SelectItem>
                  <SelectItem value="today">Hoje</SelectItem>
                  <SelectItem value="yesterday">Ontem</SelectItem>
                  <SelectItem value="last_week">Última semana</SelectItem>
                  <SelectItem value="last_month">Último mês</SelectItem>
                  <SelectItem value="last_3_months">Últimos 3 meses</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setStageFilter("all");
                  setDateFilter("all");
                }}
                className="w-full bg-white/60 border-white/30 hover:bg-white/80"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Stats Overview - responsive */}
      <div
        className={`grid gap-4 mb-6 ${
          isMobile
            ? "grid-cols-2"
            : isTablet
              ? "grid-cols-3 md:grid-cols-4"
              : "grid-cols-2 md:grid-cols-4 lg:grid-cols-7"
        }`}
      >
        {stages.map((stage) => {
          const stats = getStageStats(stage.id);
          return (
            <Card
              key={stage.id}
              className="glass border-white/20"
              data-testid={`stage-stats-${stage.id}`}
            >
              <CardContent
                className={`text-center ${isMobile ? "p-3" : "p-4"}`}
              >
                <div
                  className={`w-8 h-8 rounded-full bg-gradient-to-br ${stage.color} mx-auto mb-2 flex items-center justify-center text-white text-sm font-bold`}
                >
                  {stats.count}
                </div>
                <p className="text-xs font-medium text-slate-700">
                  {stage.label}
                </p>
                <p className="text-xs text-slate-500">{stats.percentage}%</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search and Filters for Mobile */}
      {isMobile && (
        <Card className="glass border-white/20 mb-4">
          <CardContent className="p-4">
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Buscar leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              />
              <div className="flex justify-between items-center text-sm text-slate-600">
                <span>{leads.length} leads</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refresh}
                  disabled={loading}
                >
                  🔄 Atualizar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pipeline Kanban Board - responsive */}
      <div
        className={`grid gap-4 ${
          isMobile
            ? "grid-cols-1"
            : isTablet
              ? "grid-cols-2"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7"
        }`}
      >
        {stages.map((stage) => (
          <div
            key={stage.id}
            className="kanban-column rounded-xl p-0 bg-white border shadow-sm hover:shadow-md transition-shadow duration-200"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.id)}
            data-testid={`pipeline-stage-${stage.id}`}
          >
            {/* Stage Header com gradiente moderno */}
            <div
              className={`flex items-center justify-between mb-4 p-3 rounded-t-xl bg-gradient-to-r ${stage.color} text-white shadow-lg`}
            >
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                  <span className="text-xs font-bold">
                    {getLeadsByStage(stage.id).length}
                  </span>
                </div>
                <h3 className="font-semibold text-white">{stage.label}</h3>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-xs bg-white/20 backdrop-blur rounded-full px-2 py-1">
                  {(
                    (getLeadsByStage(stage.id).length /
                      Math.max(leads.length, 1)) *
                    100
                  ).toFixed(0)}
                  %
                </div>
                {stage.rawId && !stage.isLastStage && (
                  <button
                    type="button"
                    onClick={() => openAutomationDialog(stage)}
                    title={
                      stage.autoAdvanceEnabled
                        ? `Automação ativa: avança sozinho após ${stage.autoAdvanceDays} dia(s) parado`
                        : "Configurar avanço automático desta etapa"
                    }
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors ${
                      stage.autoAdvanceEnabled
                        ? "bg-white text-slate-700"
                        : "bg-white/20 hover:bg-white/30 text-white"
                    }`}
                    data-testid={`stage-automation-btn-${stage.id}`}
                  >
                    {stage.autoAdvanceEnabled ? "⏱️" : "⚙️"}
                  </button>
                )}
              </div>
            </div>

            {/* Stage Content */}
            <div
              className="space-y-3 min-h-[400px] p-4"
              data-testid={`stage-content-${stage.id}`}
            >
              {getLeadsByStage(stage.id).map((lead) => (
                <div
                  key={lead.id}
                  className="kanban-card rounded-lg p-4 cursor-grab active:cursor-grabbing"
                  draggable
                  onDragStart={(e) => handleDragStart(e, lead)}
                  data-testid={`pipeline-lead-${lead.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-slate-900 text-sm leading-tight">
                      {lead.name}
                    </h4>
                    {lead.score && lead.score > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {lead.score}★
                      </Badge>
                    )}
                  </div>

                  <p className="text-xs break-words text-slate-600 mb-2">
                    {lead.email}
                  </p>

                  {lead.company && (
                    <p className="text-xs text-slate-500 mb-2">
                      🏢 {lead.company}
                    </p>
                  )}
                  
                  {lead.segment && (
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 mb-2">
                      🏷️ {lead.segment}
                    </span>
                  )}
                  
                  <div className="flex flex-wrap items-center justify-between text-xs">
                    <span className="text-slate-400">
                      {new Date(lead.created_at).toLocaleDateString("pt-BR")}
                    </span>
                    <div className="flex items-center space-x-1">
                      <div
                        className={`w-2 h-2 rounded-full bg-gradient-to-r ${stage.color}`}
                      ></div>
                      <span className="text-slate-500 capitalize">
                        {lead.source}
                      </span>
                    </div>
                  </div>

                  {/* Move to another stage — works on touch/mobile where drag-and-drop is unavailable */}
                  <div className="mt-3 pt-2 border-t border-slate-100">
                    <select
                      value={stage.id}
                      onChange={(e) => moveLeadToStage(lead, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      title="Mover lead para outro estágio"
                      data-testid={`move-lead-stage-${lead.id}`}
                      className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-600"
                    >
                      {stages.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.id === stage.id ? `📍 ${s.label}` : `Mover para ${s.label}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Lead Actions */}
                  <div className="flex items-center justify-end space-x-1 mt-2 pt-2 border-t border-slate-100">
                    <button
                      className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                      title="Ver detalhes"
                      onClick={() => handleViewLeadDetails(lead)}
                      data-testid={`view-lead-details-${lead.id}`}
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>

                    <button
                      className="p-1 text-slate-400 hover:text-green-600 transition-colors"
                      title="Adicionar interação"
                      onClick={() => handleAddInteraction(lead)}
                      data-testid={`add-interaction-${lead.id}`}
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              {/* Empty State */}
              {getLeadsByStage(stage.id).length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <svg
                    className="w-8 h-8 mx-auto mb-2 opacity-50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <p className="text-xs">Nenhum lead</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No pagination needed for Kanban view */}

      <div className="mt-8">
        <h4 className="font-semibold text-slate-800 mb-2">
          Como usar o Pipeline
        </h4>
        <div className="text-sm text-slate-600 space-y-1">
          <p>
            • <strong>Arraste e solte</strong> os leads entre os estágios para
            atualizar seu status (ou use o seletor "Mover para" no card, no
            celular)
          </p>
          <p>
            • <strong>Clique nos ícones</strong> dos cards para ver detalhes ou
            adicionar interações
          </p>
          <p>
            • <strong>Use as estatísticas</strong> acima para ter uma visão
            rápida da distribuição
          </p>
          <p>
            • <strong>Atualize regularmente</strong> para manter o pipeline
            sempre preciso
          </p>
        </div>
      </div>

      {/* Drag Overlay */}
      {draggedLead && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
          <div className="bg-blue-100 border-2 border-blue-300 border-dashed rounded-lg p-4 shadow-lg">
            <p className="text-blue-700 font-medium">
              Movendo: {draggedLead.name}
            </p>
          </div>
        </div>
      )}

      {/* Lead Details Modal */}
      <Dialog
        open={showLeadDetailsModal}
        onOpenChange={setShowLeadDetailsModal}
      >
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Lead</DialogTitle>
            <DialogDescription>
              Informações completas e atividades do lead
            </DialogDescription>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-6">
              {/* Lead Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Nome
                  </Label>
                  <p className="text-sm text-slate-900 mt-1">
                    {selectedLead.name}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Email
                  </Label>
                  <p className="text-sm text-slate-900 mt-1">
                    {selectedLead.email}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Telefone
                  </Label>
                  <p className="text-sm text-slate-900 mt-1">
                    {selectedLead.phone || "Não informado"}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Empresa
                  </Label>
                  <p className="text-sm text-slate-900 mt-1">
                    {selectedLead.company || "Não informado"}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Status
                  </Label>
                  <p className="text-sm text-slate-900 mt-1 capitalize">
                    {selectedLead.status}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Score
                  </Label>
                  <p className="text-sm text-slate-900 mt-1">
                    {selectedLead.score || 0}★
                  </p>
                </div>
              </div>

              {/* Notes */}
              {selectedLead.notes && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    Observações
                  </Label>
                  <p className="text-sm text-slate-900 mt-1 bg-slate-50 p-3 rounded">
                    {selectedLead.notes}
                  </p>
                </div>
              )}

              {/* Activities */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                  Atividades Recentes
                </Label>
                {leadActivities.length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {leadActivities.map((activity, index) => (
                      <div
                        key={index}
                        className="bg-slate-50 p-3 rounded border-l-4 border-blue-500"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-600 capitalize">
                            {activity.type}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(activity.created_at).toLocaleDateString(
                              "pt-BR",
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-slate-900 mt-1">
                          {activity.description}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">
                    Nenhuma atividade registrada
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Interaction Modal */}
      <Dialog
        open={showAddInteractionModal}
        onOpenChange={setShowAddInteractionModal}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Interação</DialogTitle>
            <DialogDescription>
              Registre uma nova interação com {selectedLead?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="interaction-type" className="text-sm font-medium">
                Tipo de Interação
              </Label>
              <Select
                value={newInteraction.type}
                onValueChange={(value) =>
                  setNewInteraction((prev) => ({ ...prev, type: value }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Ligação</SelectItem>
                  <SelectItem value="email">E-mail</SelectItem>
                  <SelectItem value="meeting">Reunião</SelectItem>
                  <SelectItem value="demo">Demonstração</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label
                htmlFor="interaction-description"
                className="text-sm font-medium"
              >
                Descrição *
              </Label>
              <Textarea
                id="interaction-description"
                value={newInteraction.description}
                onChange={(e) =>
                  setNewInteraction((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Descreva o que foi discutido ou planejado..."
                rows={3}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label
                  htmlFor="interaction-date"
                  className="text-sm font-medium"
                >
                  Data (opcional)
                </Label>
                <Input
                  id="interaction-date"
                  type="date"
                  value={newInteraction.scheduled_date}
                  onChange={(e) =>
                    setNewInteraction((prev) => ({
                      ...prev,
                      scheduled_date: e.target.value,
                    }))
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label
                  htmlFor="interaction-time"
                  className="text-sm font-medium"
                >
                  Horário (opcional)
                </Label>
                <Input
                  id="interaction-time"
                  type="time"
                  value={newInteraction.scheduled_time}
                  onChange={(e) =>
                    setNewInteraction((prev) => ({
                      ...prev,
                      scheduled_time: e.target.value,
                    }))
                  }
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddInteractionModal(false)}
            >
              Cancelar
            </Button>
            <Button onClick={handleSaveInteraction}>Salvar Interação</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Automation Config Modal */}
      <Dialog
        open={!!automationDialogStage}
        onOpenChange={(open) => !open && setAutomationDialogStage(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>⚡ Automação da Etapa</DialogTitle>
            <DialogDescription>
              {automationDialogStage && (
                <>
                  Mova leads de{" "}
                  <strong>"{automationDialogStage.label}"</strong>{" "}
                  automaticamente para a próxima etapa quando ficarem parados
                  tempo demais sem mudar de status.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium text-slate-800">
                  Avanço automático
                </p>
                <p className="text-xs text-slate-500">
                  Ativar avanço sozinho para a próxima etapa
                </p>
              </div>
              <Switch
                checked={automationForm.enabled}
                onCheckedChange={(checked) =>
                  setAutomationForm((prev) => ({ ...prev, enabled: checked }))
                }
                data-testid="automation-enabled-switch"
              />
            </div>

            {automationForm.enabled && (
              <div>
                <Label htmlFor="automation-days">
                  Dias parado nesta etapa antes de avançar
                </Label>
                <Input
                  id="automation-days"
                  type="number"
                  min={1}
                  value={automationForm.days}
                  onChange={(e) =>
                    setAutomationForm((prev) => ({
                      ...prev,
                      days: e.target.value,
                    }))
                  }
                  className="mt-1"
                  data-testid="automation-days-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Se o lead não mudar de status por esse número de dias, o
                  sistema move ele sozinho para a próxima etapa e registra
                  isso no histórico.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAutomationDialogStage(null)}
              disabled={savingAutomation}
            >
              Cancelar
            </Button>
            <Button onClick={saveAutomationConfig} disabled={savingAutomation}>
              {savingAutomation ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
